import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const FunnelChart = ({
  title,
  data,
  nameKey = 'stage',
  valueKey = 'count',
  height = 400,
  colors = ['#003366', '#1a4d7a', '#33668f', '#4d80a3', '#6699b8'],
  loading = false
}) => {
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = data.length > 0
        ? ((payload[0].value / data[0][valueKey]) * 100).toFixed(1)
        : 0;

      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" fontWeight="bold" mb={0.5}>
            {payload[0].payload[nameKey]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Count: {payload[0].value.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight="medium">
            {percentage}% of initial
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomLabel = ({ x, y, width, height, value, index }) => {
    const percentage = data.length > 0 && data[0][valueKey] > 0
      ? ((value / data[0][valueKey]) * 100).toFixed(1)
      : 0;

    return (
      <g>
        <text
          x={x + width + 10}
          y={y + height / 2}
          fill="#666"
          textAnchor="start"
          dominantBaseline="middle"
          fontSize="12"
        >
          {value.toLocaleString()} ({percentage}%)
        </text>
      </g>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={height}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          {title}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Conversion funnel showing progression through stages
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 80, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis type="number" tick={{ fontSize: 12 }} stroke="#666" />
            <YAxis
              type="category"
              dataKey={nameKey}
              tick={{ fontSize: 12 }}
              stroke="#666"
              width={90}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey={valueKey}
              label={<CustomLabel />}
              radius={[0, 8, 8, 0]}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Conversion Rate Summary */}
        {data.length > 1 && data[0][valueKey] > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Overall Conversion Rate:{' '}
              <Typography component="span" variant="body2" fontWeight="bold" color="primary.main">
                {((data[data.length - 1][valueKey] / data[0][valueKey]) * 100).toFixed(1)}%
              </Typography>
              {' '}({data[data.length - 1][valueKey]} / {data[0][valueKey]})
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FunnelChart;
